import { colors } from '@/constants/colors'
import { type ScoreSlot, heightOf } from '@/features/care/scoreSeries'
import { Header } from '@/ui/core/header'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * The tints a slot with nothing in it is drawn in.
 *
 * The same pair `ScoreReading` uses for its waiting state, so the two ways this app draws "no
 * score yet" are the same grey rather than two greys that nearly match. The bar is a step up from
 * the type, because a shape needs more of a tint to register than a numeral does.
 */
const GHOST = {
	type: 'rgba(125,111,111,0.22)',
	bar: 'rgba(125,111,111,0.13)',
}

/**
 * How tall the bars are, and what stands above and below them.
 *
 * Written as points rather than Tailwind steps: this rem is fourteen, so a chart laid out in
 * steps would be measured in multiples of a number nobody chose. The three together decide the
 * card's height, and the card's height must not change when the first reading lands — which is
 * why an empty slot still draws a dash where its numeral goes and a label where its date goes.
 */
const BARS = 86
const NUMERAL_GAP = 6
const LABEL_GAP = 7

/**
 * The mount the chart is set in, and its own radius.
 *
 * The same rule the visit card's photograph follows: a twelve-point mount, and the inner radius is
 * the card's own less the mount, so the two curves are concentric rather than merely both round.
 * The card carries `MOUNT` as its padding for that to hold, which is why the label row takes two
 * points of its own back — the label sits where fourteen points of card padding used to put it.
 *
 * `PANEL` is `surface`, the token named for exactly this: the quiet grey ground of a white
 * surface's inner panel. It is the app's existing value rather than a new one, and it is light
 * enough that the coral keeps every bit of its contrast against it.
 */
const MOUNT = 12
const CARD_RADIUS = 24
const PANEL_RADIUS = CARD_RADIUS - MOUNT
const LABEL_INSET = 2
const PANEL = colors.surface

/**
 * The air between the label row and the chart.
 *
 * Twenty-two, not the ten it opened with: the numerals sat on the row above them and the card
 * read as one crowded block over a pool of empty white. Twelve of it is the panel's own top
 * padding now, so the gap is split between the mount and the field rather than being one drop.
 */
const HEAD_GAP = 10
const PANEL_TOP = 12
const PANEL_BOTTOM = 11

/** The shortest a real bar may be drawn, so a score at the floor is still a bar and not a line. */
const MIN_BAR = 16

/** What a slot with no reading in it puts where the numeral goes. */
const NO_READING = '—'

/** The card's own padding, so the mount is one number in one place. */
export const CARD_MOUNT = MOUNT
/** The inset the label row takes back, so it sits where the old padding put it. */
export const CARD_LABEL_INSET = LABEL_INSET

const Bar = ({ slot }: { slot: ScoreSlot }) => {
	const ghost = slot.kind === 'ghost'
	const height = ghost ? heightOf(slot) * BARS : Math.max(MIN_BAR, heightOf(slot) * BARS)

	return (
		<View
			style={{
				height,
				borderRadius: 6,
				backgroundColor: ghost ? GHOST.bar : colors.accent,
			}}
		/>
	)
}

/**
 * One reading's numeral, or a dash in place of one.
 *
 * Every figure is the same size in the same face, whatever the slot: the newest reading is in ink
 * and the rest are in the muted grey a second line takes, and that difference in colour is the
 * only hierarchy on the card. Nothing here is bigger than anything else, because the card used to
 * carry a seventy-point figure and a chart, and one of them had to stop shouting.
 */
const Numeral = ({ slot }: { slot: ScoreSlot }) => (
	<Header
		variant="h4"
		className="text-center text-[19px] leading-[19px]"
		style={{
			color: slot.kind === 'ghost' ? GHOST.type : slot.newest ? colors.ink : colors.muted,
		}}
	>
		{slot.kind === 'ghost' ? NO_READING : String(slot.score)}
	</Header>
)

/**
 * The last four readings, oldest at the left.
 *
 * Left to right, because that is the direction every time series a member has ever seen runs in,
 * and it puts the newest reading nearest the edge the eye finishes on. Slots are evenly spaced
 * rather than laid out in time: visits are irregular, and an axis of months would need empty
 * space that means nothing between a fourteen-month gap, and would collide two visits three weeks
 * apart. Each bar carries its own date instead, which is the honest way to space them evenly.
 */
export const ScoreChart = ({ slots }: { slots: ScoreSlot[] }) => (
	<View
		className="flex-row items-end"
		style={{
			gap: 10,
			marginTop: HEAD_GAP,
			backgroundColor: PANEL,
			borderRadius: PANEL_RADIUS,
			paddingTop: PANEL_TOP,
			paddingBottom: PANEL_BOTTOM,
			paddingHorizontal: MOUNT,
		}}
	>
		{slots.map((slot, index) => (
			<View
				// The slots are positions rather than things — the same date can arrive in a
				// different one as the window slides — so the index is what identifies them.
				// biome-ignore lint/suspicious/noArrayIndexKey: a slot *is* its position
				key={index}
				className="flex-1 justify-end"
			>
				<View style={{ marginBottom: NUMERAL_GAP }}>
					<Numeral slot={slot} />
				</View>
				<Bar slot={slot} />
				<Text
					size="body3"
					className="text-center uppercase"
					style={{
						marginTop: LABEL_GAP,
						color: slot.kind === 'ghost' ? GHOST.type : colors.muted,
					}}
				>
					{slot.label}
				</Text>
			</View>
		))}
	</View>
)
